class PredictiveSearch extends HTMLElement {
	constructor() {
		super()

		this.selectors = {
			input: '[data-predictive-search-input]',
			form: 'form',
			formStatus: '[data-predictive-search-status]',
			predictiveSearchResults: '[data-predictive-search]',
			closeBtn: '.f-drawer__close'
		}

		this.domNodes = queryDomNodes(this.selectors, this)
		this.drawer = this.closest('drawer-component')
		this.input = this.domNodes.input
		this.form = this.domNodes.form
		this.formStatus = this.domNodes.formStatus
		this.predictiveSearchResults = this.domNodes.predictiveSearchResults
		this.cachedResults = {}
		this.isOpen = false
		this.predictive_search_url = window.FoxThemeSettings.routes.predictive_search_url
		this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
		this.searchTerm = '';

		this.setupEventListeners()
	}

	setupEventListeners() {
		this.form.addEventListener('submit', this.onFormSubmit.bind(this))

		this.input.addEventListener('input', debounce((event) => {
			this.onChange(event)
		}, 300).bind(this))
		this.input.addEventListener('focus', this.onFocus.bind(this))
		this.addEventListener('focusout', this.onFocusOut.bind(this))
		this.addEventListener('keyup', this.onKeyup.bind(this))
		this.addEventListener('keydown', this.onKeydown.bind(this))
		this.domNodes.closeBtn && this.domNodes.closeBtn.addEventListener('click', this.onClose.bind(this))
	}

	getQuery() {
		return this.input.value.trim()
	}

	onChange() {
		const newSearchTerm = this.getQuery();

		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#f-predictive-search-results")?.remove();
    }

		// Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
	}

	onFormSubmit(e) {
		if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) e.preventDefault()
	}

	onFocus() {    
		const currentSearchTerm = this.getQuery();

		if (!currentSearchTerm.length) return;

		if (this.searchTerm !== currentSearchTerm) {
			this.onChange();
		} else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
	}

	onFocusOut(e) {
		setTimeout(() => {
			if (!this.contains(document.activeElement)) this.close();
		})
	}

	onClose(e) {
		this.close(true)
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true)
		event.preventDefault()

		switch (event.code) {
			case 'ArrowUp':
				this.switchOption('up')
				break
			case 'ArrowDown':
				this.switchOption('down')
				break
			case 'Enter':
				this.selectOption()
				break
		}
	}

	onKeydown(e) {
		if (
			e.code === 'ArrowUp' ||
			e.code === 'ArrowDown'
		) {
			e.preventDefault()
		}
	}

	switchOption(direction) {
		if (!this.getAttribute('open')) return

		const moveUp = direction === 'up'
		const selectedElement = this.querySelector('[aria-selected="true"]')
		const allElements = this.querySelectorAll('li')
		let activeElement = this.querySelector('li')

		if (moveUp && !selectedElement) return

		this.formStatus.textContent = ''

		if (!moveUp && selectedElement) {
			activeElement = selectedElement.nextElementSibling || allElements[0]
		} else if (moveUp) {
			activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1]
		}

		if (activeElement === selectedElement) return

		activeElement.setAttribute('aria-selected', true)
		if (selectedElement) selectedElement.setAttribute('aria-selected', false)

		this.setLiveRegionText(activeElement.textContent)
		this.input.setAttribute('aria-activedescendant', activeElement.id)
	}

	selectOption() {
		const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button')

		if (selectedProduct) selectedProduct.click()
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(" ", "-").toLowerCase()
		this.setLiveRegionLoadingState()

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey])
			return
		}
		const url = `${this.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`
		// 상품 자동완성은 자체 검색엔진(/v1/suggest) — 결과 페이지와 동일 인덱스·랭킹.
		// 검색어 제안(Suggestions)·문구·마크업 틀은 네이티브 응답을 그대로 쓰고,
		// Products 그룹의 목록만 교체한다. 자체 API 실패 시 전체 네이티브 폴백.
		const native = fetch(url).then((res) => {
			if (!res.ok) {
				var error = new Error(res.status)
				this.close()
				throw error
			}
			return res.text()
		})
		const ours = this.fetchOwnSuggest(searchTerm)
		Promise.all([native, ours])
			.then(([text, products]) => {
				const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search')
				if (products) this.swapProductsGroup(section, products)
				const resultsMarkup = section.innerHTML

				// Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );

				this.renderSearchResults(resultsMarkup)
			})
			.catch((error) => {
				this.close()
				throw error
			})
	}

	/** 자체 suggest 호출 — 실패·타임아웃이면 null (네이티브 폴백) */
	fetchOwnSuggest(searchTerm) {
		let base = this.getAttribute('data-suggest-api')
		if (!base) return Promise.resolve(null)
		// 로컬 theme dev(http)에서 stage 자가서명 인증서 회피 — 검색 위젯과 동일
		if (location.protocol === 'http:' && base.includes('.trenbekr.in')) {
			base = base.replace('https://', 'http://')
		}
		const locale = (document.documentElement.lang || '').split('-')[0]
		const qs = new URLSearchParams({ q: searchTerm })
		if (locale) qs.set('locale', locale)
		return fetch(`${base}/v1/suggest?${qs}`, { signal: AbortSignal.timeout(4000) })
			.then((res) => (res.ok ? res.json() : null))
			.then((json) => (json && Array.isArray(json.products) ? json.products : null))
			.catch(() => null)
	}

	/** 네이티브 섹션 마크업에서 Products 그룹의 목록만 자체 결과로 교체 */
	swapProductsGroup(section, products) {
		const resultsRoot = section.querySelector('#f-predictive-search-results')
		const nativeGroup = section.querySelector('.f-predictive-search__result-products')
		if (products.length === 0) {
			if (nativeGroup) nativeGroup.remove()
			return
		}
		const items = products.map((p, i) => this.suggestItemHtml(p, i + 1)).join('')
		if (nativeGroup) {
			// 헤딩(문구·스피너·i18n)은 네이티브 그대로, 목록만 교체
			const list = nativeGroup.querySelector('ul')
			if (list) list.innerHTML = items
			return
		}
		if (!resultsRoot) return
		// 네이티브가 상품 0건인데 우리는 있는 경우 — 그룹을 새로 만든다
		const group = `
<div class="f-predictive-search__result-group f-predictive-search__result-products f-predictive-search__block-products">
  <h2 id="f-predictive-search-products" class="f-predictive-search__heading font-body-bolder">Products</h2>
  <ul id="f-predictive-search-results-list" class="f-predictive-search__results-list list-none" role="listbox" aria-labelledby="predictive-search-products">${items}</ul>
</div>`
		const loading = resultsRoot.querySelector('.f-predictive-search__loading-state')
		if (loading) loading.insertAdjacentHTML('beforebegin', group)
		else resultsRoot.insertAdjacentHTML('beforeend', group)
	}

	/** 네이티브 predictive-search.liquid 의 상품 아이템과 동일 마크업 */
	suggestItemHtml(p, index) {
		const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])
		const money = (usd) => `$${Number(usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`
		const url = p.url || `/products/${p.handle}`
		const img = p.image ? `${p.image}${p.image.includes('?') ? '&' : '?'}width=150` : ''
		const showVendor = this.getAttribute('data-show-vendor') === 'true'
		const showPrice = this.getAttribute('data-show-price') === 'true'
		const titleClass = this.getAttribute('data-title-class') || 'text-product-title font-body'
		const hover = this.getAttribute('data-hover-effect') || 'zoom'
		// compare_at_price 는 {usd,krw} 객체 — usd 로 비교
		const cmp = p.compare_at_price?.usd
		const onSale = (cmp && cmp > p.price.usd) || p.discount_rate > 0
		const price = !showPrice
			? ''
			: onSale
				? `<div class="f-price f-price--on-sale"><div class="f-price__sale"><span class="f-price-item f-price-item--sale"><span class="money">${money(p.price.usd)}</span></span>${cmp ? ` <s class="f-price-item f-price-item--regular"><span class="money">${money(cmp)}</span></s>` : ''}</div></div>`
				: `<div class="f-price"><div class="f-price__regular"><span class="f-price-item f-price-item--regular"><span class="money">${money(p.price.usd)}</span></span></div></div>`
		return `
<li id="f-predictive-search-option-${index}" role="option" aria-selected="false">
  <a href="${esc(url)}" class="f-predictive-search__item f-image-hover-effect__${esc(hover)}" tabindex="-1">
    ${img ? `<div class="f-predictive-search__item-image f-image" style="--aspect-ratio: 1"><img src="${esc(img)}" alt="${esc(p.title)}" width="70" height="70" loading="lazy"></div>` : ''}
    <div class="f-predictive-search__item-content">
      ${showVendor ? `<span class="visually-hidden">Vendor:</span><div class="f-predictive-search__item-vendor text-subtext">${esc(p.brand)}</div>` : ''}
      <h3 class="f-predictive-search__item-heading ${esc(titleClass)}">${esc(p.title)}</h3>
      ${price}
    </div>
  </a>
</li>`
	}

	setLiveRegionLoadingState() {
		this.loadingText = this.loadingText || this.getAttribute('data-loading-text')

		this.setLiveRegionText(this.loadingText)
		this.setAttribute('loading', true)
	}

	setLiveRegionText(statusText) {
		this.formStatus.setAttribute('aria-hidden', 'false')
		this.formStatus.textContent = statusText

		setTimeout(() => {
			this.formStatus.setAttribute('aria-hidden', 'true')
		}, 1000)
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup
		this.setAttribute('results', true)

		this.setLiveRegionResults()
		this.open()
		window.bucksCC?.reConvert?.() // 통화앱 KRW 변환 (자체 렌더 상품 가격)
	}

	setLiveRegionResults() {
		this.removeAttribute('loading')
		this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent)
	}

	updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

	open() {
		this.setAttribute('open', true)
		this.input.setAttribute('aria-expanded', true)
		this.isOpen = true
	}

	close(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = ''
			this.removeAttribute('results')
		}

		const selected = this.querySelector('[aria-selected="true"]')

		if (selected) selected.setAttribute('aria-selected', false)

		this.input.setAttribute('aria-activedescendant', '')
		this.removeAttribute('open')
		this.input.setAttribute('aria-expanded', false)
		this.predictiveSearchResults.removeAttribute('style')

		this.isOpen = false
	}
}

customElements.define('predictive-search', PredictiveSearch)
